// The organisation's projects, in the workbench's chrome. Signed in but in
// no organisation yet (GIOTHUB-186): the invitation hint instead.
import { useEffect, useState } from "preact/hooks";
import { api } from "../lib/api";
import type { Me, Project } from "../lib/types";
import { Icon } from "../workbench/icons";
import { Centered } from "../App";
import { fmtDateTime, t, useLang } from "../i18n";
import { Shell } from "./Shell";

export function useMe(): [Me | null, string] {
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api<Me>("GET", "/me").then(setMe).catch((e) => setErr(e.message));
  }, []);
  return [me, err];
}

export function NoOrg({ me }: { me: Me }) {
  return (
    <Shell me={me} active="projects">
      <main class="mx-auto grid w-full max-w-[640px] flex-1 content-center gap-4 p-6">
        <h1 class="text-[20px] font-semibold">{t("noorg.title")}</h1>
        <p class="text-muted">{t("noorg.text", { email: me.email || me.user_id })}</p>
        <p class="text-muted">{t("noorg.invite")}</p>
        {me.orgs.length > 0 && <p class="text-warn">{t("noorg.disabled")}</p>}
      </main>
    </Shell>
  );
}

export function Projects() {
  useLang();
  const [me, meErr] = useMe();
  const [list, setList] = useState<Project[] | null>(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", type_id: "", scenario: "A" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (me?.organisation) api<Project[]>("GET", "/projects").then(setList).catch((e) => setErr(e.message));
  }, [me]);
  if (meErr || err) return <Centered>{meErr || err}</Centered>;
  if (!me) return <Centered>{t("common.loading")}</Centered>;
  if (!me.organisation) return <NoOrg me={me} />;
  if (!list) return <Centered>{t("common.loading")}</Centered>;
  const create = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = await api<Project>("POST", "/projects", form);
      location.href = `/p/${p.id}`;
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const canBuild = me.role === "builder" || me.role === "admin";
  const kind = (s: string) => (s === "C" ? t("projects.kind.c") : s === "B" ? t("projects.kind.b") : t("projects.kind.a"));
  return (
    <Shell me={me} active="projects">
      <main class="mx-auto grid w-full max-w-[1400px] flex-1 grid-cols-1 gap-6 overflow-y-auto p-4 sm:p-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section>
          <h1 class="mb-4 text-[20px] font-semibold">{t("projects.title")}</h1>
          {list.length === 0 && <p class="text-muted">{t("projects.none")}</p>}
          <div class="grid gap-2">
            {list.map((p) => (
              <a href={`/p/${p.id}`} class="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-line bg-side px-4 py-3 text-ink no-underline hover:border-line2 hover:bg-hover">
                <span class="font-semibold">{p.name}</span>
                <span class="chip">{p.type_id}</span>
                <span class="text-[12px] text-muted">{t("projects.scenario", { s: p.scenario })} · {kind(p.scenario)} · {p.publish_target === "git" ? t("projects.target.git") : t("projects.target.store")}</span>
                <span class="flex-1" />
                <span class="font-mono text-[12px] text-dim">r{p.head_rev} · {fmtDateTime(p.updated_at)}</span>
              </a>
            ))}
          </div>
        </section>
        {canBuild && (
          <form onSubmit={create} class="h-fit rounded-xl border border-line bg-side p-5">
            <h2 class="mb-1 text-[15px] font-semibold">{t("projects.new")}</h2>
            <fieldset class="mb-4 flex flex-col gap-2 border-0 p-0">
              <legend class="mb-1 text-[12px] font-semibold text-ink2">{t("projects.how")}</legend>
              {([["A", t("projects.a.title"), t("projects.a.text")],
                ["C", t("projects.c.title"), t("projects.c.text")],
                ["B", t("projects.b.title"), t("projects.b.text")]] as const).map(([v, ti, d]) => (
                <label class={`flex gap-2.5 rounded-lg border p-2.5 ${form.scenario === v ? "border-accent bg-sel/40" : "border-line2"} cursor-pointer`}>
                  <input type="radio" name="scenario" value={v} checked={form.scenario === v} onChange={() => setForm({ ...form, scenario: v })} />
                  <span><span class="block text-[13px] font-semibold">{v} · {ti}</span><span class="block text-[12px] text-muted">{d}</span></span>
                </label>
              ))}
            </fieldset>
            <label class="mb-1 block text-[12px] font-semibold text-ink2" for="np-name">{t("projects.name")}</label>
            <input id="np-name" class="field mb-3" required value={form.name} placeholder="GimasiLM"
              onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })} />
            <label class="mb-1 block text-[12px] font-semibold text-ink2" for="np-id">{t("projects.typeid")}</label>
            <input id="np-id" class="field mb-4 font-mono" required pattern="[a-z0-9]+(\.[a-z0-9][a-z0-9_\-]*)+" value={form.type_id}
              placeholder="ch.gimasi.lm" onInput={(e) => setForm({ ...form, type_id: (e.target as HTMLInputElement).value })} />
            <button type="submit" class="btn btn-pri w-full justify-center" disabled={busy}><Icon name="plus" size={14} stroke={2.2} />{t("projects.create")}</button>
          </form>
        )}
      </main>
    </Shell>
  );
}
